/**
 * Messaging redesign — the motion, as Web Animations on real elements.
 * Handoff: docs/plans/2026-09-messaging-redesign/README.md, "Interactions and motion".
 *
 * Every helper here is imperative and DOM-only: React owns what is on screen, these own how
 * it arrives. Each one no-ops cleanly when the element is missing or `element.animate` is
 * (jsdom), and every entrance is a one-shot with no fill, so nothing here can leave a
 * bubble stuck half-way if a frame is dropped.
 */
import ringArt from '../../assets/img/portal-ring.png';
import { prefersReducedMotion } from '../transitions/portalMotion';
import { EASE, PORTAL_RING_PX, coverRadius, BAND_RATIO } from './messagesModel';
import { portalNavigate } from '../transitions/portalNavigate';

const can = (el) => !!el && typeof el.animate === 'function';

/** A spinning ring element, placed by the caller. */
export function makeRing(size, { fast = true } = {}) {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = `position:absolute;left:0;top:0;width:${size}px;height:${size}px;pointer-events:none;will-change:transform,opacity;`;
  const art = document.createElement('span');
  art.style.cssText = `position:absolute;inset:0;background:url(${ringArt}) 0 0/100% 100% no-repeat;display:block;`;
  if (!prefersReducedMotion()) art.style.animation = `prSpin ${fast ? '.9s' : '16s'} linear infinite`;
  el.appendChild(art);
  return el;
}

/**
 * The doorway: clone what is showing, change state underneath, reveal the new surface
 * through the ring's hole.
 *
 * @param region   the box the transition happens in (position: relative; overflow: hidden)
 * @param live     the element inside it that React re-renders (both surfaces live here)
 * @param origin   { x, y } in the region's own coordinates
 * @param back     reverse: the surface shrinks back into the ring
 * @param doorway  the ring starts at scale 1 over a tapped 56px ring
 * @param apply    (afterCommit) => void — swap the state, then call afterCommit once the DOM
 *                 shows the new surface
 * @returns a promise that resolves when the region is clean again
 */
export function runPortalTransition({ region, live, origin, back = false, doorway = false, ringPx = PORTAL_RING_PX, apply, load, onFailure }) {
  return portalNavigate({
    origin, back, doorway, ringSize: ringPx, load, onFailure,
    surface: region && live ? { region, live } : undefined,
    commit: result => apply(() => {}, result),
  });
}

/** Rows slide from where they were to where they are (FLIP), the bumped one on top. */
export function flipRows({ rows, before, bumpedId }) {
  if (prefersReducedMotion()) return;
  rows.forEach((el) => {
    const id = el.dataset.tid;
    const was = before.get(id);
    if (was == null) return;
    const dy = was - el.offsetTop;
    if (Math.abs(dy) < 1 || !can(el)) return;
    el.style.zIndex = id === bumpedId ? '3' : '1';
    const an = el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], { duration: 420, easing: EASE.SLIDE });
    an.onfinish = () => { el.style.zIndex = ''; };
  });
}

/** The unread badge pops when its count goes up. */
export function popBadge(el) {
  if (!can(el) || prefersReducedMotion()) return;
  el.animate([{ transform: 'scale(0)' }, { transform: 'scale(1.25)', offset: 0.7 }, { transform: 'scale(1)' }], { duration: 260, easing: EASE.OUT });
}

/** A 40px ring pops at the send button. `layer` is the positioned box the ring lives in. */
export function popSendRing({ layer, button }) {
  if (!layer || !button || prefersReducedMotion()) return;
  const lr = layer.getBoundingClientRect();
  const br = button.getBoundingClientRect();
  const cx = br.left + br.width / 2 - lr.left;
  const cy = br.top + br.height / 2 - lr.top;
  const ring = makeRing(40);
  ring.style.zIndex = '40';
  layer.appendChild(ring);
  if (!can(ring)) { ring.remove(); return; }
  const at = (s) => `translate(${cx - 20}px, ${cy - 20}px) scale(${s})`;
  const an = ring.animate([{ transform: at(0.3), opacity: 1 }, { transform: at(1.9), opacity: 0 }], { duration: 480, easing: EASE.OUT });
  an.onfinish = () => ring.remove();
  setTimeout(() => ring.remove(), 600);
}

/** A sent bubble flies out of the send button to its resting place. */
export function animateSent({ bubble, button }) {
  if (!can(bubble) || prefersReducedMotion()) return;
  const tr = bubble.getBoundingClientRect();
  let dx = 0; let dy = 0;
  if (button) {
    const br = button.getBoundingClientRect();
    dx = (br.left + br.width / 2) - tr.right;
    dy = (br.top + br.height / 2) - tr.bottom;
  }
  bubble.style.transformOrigin = '100% 100%';
  bubble.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) scale(.2)`, opacity: 0, clipPath: 'circle(0% at 100% 100%)' },
      { transform: 'translate(0,0) scale(1)', opacity: 1, clipPath: 'circle(150% at 100% 100%)' },
    ],
    { duration: 380, easing: EASE.OUT },
  );
}

/** An incoming bubble reveals from its bottom-left corner. */
export function animateIncoming(bubble) {
  if (!can(bubble) || prefersReducedMotion()) return;
  bubble.animate(
    [
      { clipPath: 'circle(0px at 0% 100%)', transform: 'translateY(6px)' },
      { clipPath: 'circle(150% at 0% 100%)', transform: 'translateY(0)' },
    ],
    { duration: 340, easing: EASE.OUT },
  );
}

/** Tapping a quote pulses the original. */
export function pulse(el) {
  if (!can(el) || prefersReducedMotion()) return;
  el.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.04)' }, { transform: 'scale(1)' }, { transform: 'scale(1.03)' }, { transform: 'scale(1)' }],
    { duration: 700, easing: 'ease-in-out' },
  );
}

/** The capsule rises out of the bubble; each emoji staggers in; the menu drops in under. */
export function fanTray({ bubble, capsule, menu }) {
  if (prefersReducedMotion()) return;
  if (can(bubble)) bubble.animate([{ transform: 'scale(.97)' }, { transform: 'scale(1)' }], { duration: 220, easing: EASE.SMOOTH });
  if (can(capsule)) {
    capsule.animate([{ opacity: 0, transform: 'translateY(12px) scale(.55)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 260, easing: EASE.SMOOTH });
    Array.from(capsule.querySelectorAll('[data-fan]')).forEach((el, i) => {
      if (!can(el)) return;
      el.animate([{ opacity: 0, transform: 'translateY(10px) scale(.4)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 240, delay: 50 + i * 28, easing: EASE.SMOOTH, fill: 'backwards' });
    });
  }
  if (can(menu)) menu.animate([{ opacity: 0, transform: 'translateY(-8px) scale(.96)' }, { opacity: 1, transform: 'none' }], { duration: 220, delay: 80, easing: EASE.SMOOTH, fill: 'backwards' });
}

/** ALL REACTIONS grows out of the capsule; the first 32 cells stagger by column and row. */
export function openMore(panel) {
  if (!can(panel) || prefersReducedMotion()) return;
  panel.animate([{ opacity: 0, transform: 'scale(.6)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 280, easing: EASE.SMOOTH });
  Array.from(panel.querySelectorAll('[data-grid]')).slice(0, 32).forEach((el, i) => {
    if (!can(el)) return;
    el.animate([{ opacity: 0, transform: 'scale(.3)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 220, delay: 60 + (i % 8) * 14 + Math.floor(i / 8) * 30, easing: EASE.SMOOTH, fill: 'backwards' });
  });
}

/** Capsule, panel and menu go to nothing together; resolves when they have. */
export function closeTray(elements) {
  const els = elements.filter(can);
  if (!els.length || prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    let n = els.length;
    els.forEach((el) => {
      const an = el.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.85)' }], { duration: 120, easing: EASE.IN, fill: 'forwards' });
      an.onfinish = () => { if (--n === 0) resolve(); };
    });
    setTimeout(resolve, 200);
  });
}

/** A picked reaction flies from the tapped emoji to its badge on the bubble. */
export function flyBadge({ badge, fromRect }) {
  if (!can(badge) || prefersReducedMotion()) return;
  if (fromRect) {
    const to = badge.getBoundingClientRect();
    const dx = (fromRect.left + fromRect.width / 2) - (to.left + to.width / 2);
    const dy = (fromRect.top + fromRect.height / 2) - (to.top + to.height / 2);
    badge.animate([{ transform: `translate(${dx}px, ${dy}px) scale(1.6)` }, { transform: 'translate(0,0) scale(1)' }], { duration: 380, easing: EASE.SMOOTH });
  } else {
    badge.animate([{ transform: 'scale(.3)', opacity: 0 }, { transform: 'scale(1)', opacity: 1 }], { duration: 240, easing: EASE.SMOOTH });
  }
}

/** UNSEND: the bubble collapses into its bottom-right corner. Resolves when it has. */
export function collapseBubble(bubble) {
  if (!can(bubble) || prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    const an = bubble.animate(
      [{ clipPath: 'circle(150% at 100% 100%)', opacity: 1 }, { clipPath: 'circle(0px at 100% 100%)', opacity: 0.4 }],
      { duration: 220, easing: EASE.IN, fill: 'forwards' },
    );
    an.onfinish = resolve;
    setTimeout(resolve, 300);
  });
}

/** The reply bar and the toast rise in. `translateX` keeps a centred element centred. */
export function riseIn(el, { dy = 12, ms = 220, centred = false } = {}) {
  if (!can(el) || prefersReducedMotion()) return;
  const x = centred ? 'translateX(-50%) ' : '';
  el.animate([{ opacity: 0, transform: `${x}translateY(${dy}px)` }, { opacity: 1, transform: `${x}translateY(0)` }], { duration: ms, easing: EASE.SMOOTH });
}

/**
 * The modal out of the +: the panel is revealed from the trigger's centre through a
 * growing ring, while the scrim fades in. `overlay` is the fixed layer the ring lives in.
 */
export function revealModal({ overlay, panel, scrim, fromRect }) {
  if (!panel || !fromRect || !can(panel) || prefersReducedMotion()) return;
  const pr = panel.getBoundingClientRect();
  const ox = fromRect.left + fromRect.width / 2 - pr.left;
  const oy = fromRect.top + fromRect.height / 2 - pr.top;
  const R = coverRadius({ x: ox, y: oy }, { width: panel.offsetWidth, height: panel.offsetHeight });
  const ringPx = 56;
  const band = (ringPx / 2) * BAND_RATIO;
  const ms = 520;
  if (can(scrim)) scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 });
  panel.animate([{ clipPath: `circle(4px at ${ox}px ${oy}px)` }, { clipPath: `circle(${R}px at ${ox}px ${oy}px)` }], { duration: ms, easing: EASE.EF });
  if (!overlay) return;
  const or = overlay.getBoundingClientRect();
  const fx = fromRect.left + fromRect.width / 2 - or.left;
  const fy = fromRect.top + fromRect.height / 2 - or.top;
  const ring = makeRing(ringPx);
  ring.style.zIndex = '5';
  overlay.appendChild(ring);
  const at = (s) => `translate(${fx - ringPx / 2}px, ${fy - ringPx / 2}px) scale(${s})`;
  ring.animate([{ transform: at(0.2) }, { transform: at(R / band) }], { duration: ms, easing: EASE.EF });
  const fade = ring.animate([{ opacity: 1 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }], { duration: ms, fill: 'forwards' });
  fade.onfinish = () => ring.remove();
  setTimeout(() => ring.remove(), ms + 100);
}

/** Close: the panel fades and settles to .98 while the scrim goes. Resolves when done. */
export function dismissModal({ panel, scrim }) {
  if (!can(panel) || prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    panel.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.98)' }], { duration: 120, easing: EASE.IN, fill: 'forwards' });
    if (can(scrim)) scrim.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: 'forwards' });
    setTimeout(resolve, 130);
  });
}

/**
 * The voice note leaves its bubble and lands in the dock at the top of the thread: the
 * dock is drawn in place, then played from the bubble's box (translate + scale) to its own.
 * The bubble's card fades under it so the note reads as having moved, not copied.
 */
export function flyToDock({ dock, fromRect, fromEl }) {
  if (!can(dock) || !fromRect || prefersReducedMotion()) return;
  const to = dock.getBoundingClientRect();
  if (!to.width || !to.height) return;
  const dx = fromRect.left - to.left;
  const dy = fromRect.top - to.top;
  const sx = Math.max(0.2, fromRect.width / to.width);
  const sy = Math.max(0.2, fromRect.height / to.height);
  dock.style.transformOrigin = '0 0';
  dock.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, opacity: 0.4 },
      { transform: 'translate(0, 0) scale(1, 1)', opacity: 1 },
    ],
    { duration: 460, easing: EASE.SLIDE },
  );
  if (can(fromEl)) fromEl.animate([{ opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }], { duration: 460, easing: EASE.OUT });
}

/** The notes bubble pops in over the thread once the player is up. */
export function popVoiceBubble(el) {
  if (!can(el) || prefersReducedMotion()) return;
  el.animate(
    [
      { transform: 'scale(0.4) translateY(12px)', opacity: 0 },
      { transform: 'scale(1.12) translateY(-2px)', opacity: 1, offset: 0.7 },
      { transform: 'scale(1) translateY(0)', opacity: 1 },
    ],
    { duration: 380, easing: EASE.SMOOTH, delay: 220 },
  );
}
